/* Lumetier top nav + Products dropdown (portaled to body).
   Exported separately because Hub.jsx + ProductPageTemplate.jsx mount this
   nav on their own pages. */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import LumetierAperture from "@/components/editorial/LumetierAperture";
import { GOLD, GOLD_LIGHT, LUMETIER_PRODUCTS } from "./constants";

function ProductsDropdown() {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const reposition = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setPanelPos({ top: r.bottom + 12, left: r.left + r.width / 2 });
  }, []);

  useEffect(() => {
    if (!open) return;
    reposition();
    let raf = 0;
    function schedule() {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; reposition(); });
    }
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, { passive: true });
    return () => {
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [open, reposition]);

  useEffect(() => {
    function onClick(e) {
      const t = e.target;
      if (wrapperRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // CLICK-only pattern. Hover-bridge + onMouseLeave on the trigger was a
  // race-condition magnet (mouse traveling across the 12px gap to the
  // portaled panel fired mouseleave with relatedTarget=null and closed
  // the menu before the user could reach an item). We now toggle on click,
  // dismiss on outside-click / Escape / item-selection. Works on touch too.
  return (
    <div ref={wrapperRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-controls="lumetier-products-menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-stone-400 hover:text-stone-100 transition-colors uppercase tracking-[0.22em] text-[11px]"
        data-testid="nav-products-trigger"
      >
        Products
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && createPortal(
        <div
          id="lumetier-products-menu"
          role="menu"
          aria-labelledby="nav-products-trigger"
          ref={panelRef}
          data-testid="nav-products-dropdown"
          data-dropdown-panel="lumetier-products"
          style={{ position: "fixed", top: panelPos.top, left: panelPos.left, transform: "translateX(-50%)", zIndex: 1000 }}
        >
          <div
            className="w-[380px] bg-[#0F0E0D]/98 backdrop-blur-md border border-[color:var(--gold-foil)]/25 shadow-[0_28px_90px_-15px_rgba(0,0,0,0.85),0_0_0_1px_rgba(0,0,0,0.5)] p-3"
            style={{ borderRadius: 4 }}
          >
            {LUMETIER_PRODUCTS.map((p) => (
              <Link
                key={p.id}
                to={p.to}
                onClick={() => setOpen(false)}
                role="menuitem"
                className="flex items-start gap-3 p-3 rounded transition-colors hover:bg-white/[0.04] group"
                data-testid={`nav-product-${p.id}`}
              >
                <div
                  className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center"
                  style={{ background: p.iconBg }}
                >
                  <p.icon className="w-4.5 h-4.5" style={{ color: p.iconColor, width: 18, height: 18 }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[13px] text-stone-100 font-medium normal-case tracking-normal">
                    {p.title}
                    {!p.ready && (
                      <span className="text-[9px] uppercase tracking-[0.22em] text-stone-500 font-normal">
                        Soon
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-stone-500 leading-snug normal-case tracking-normal mt-0.5">
                    {p.short}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function LumetierNav() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    function onResize() { if (window.innerWidth >= 768) setOpen(false); }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const LINKS = [
    { href: "/marketing#features",  label: "Features" },
    { href: "/marketing#interactive", label: "Try it",  accent: true },
    { href: "/marketing#pricing",   label: "Pricing" },
  ];

  // Nav-logo navigation:
  //   • Destination is `/` — the canonical Lumetier home (`LumetierHub`).
  //     The legacy `/marketing` landing is linked separately from the footer.
  //   • If the user is already on `/`, the click is a no-op + scroll-to-top —
  //     no navigation event, no Hub remount, no Dior-intro replay risk.
  //   • The session-storage flag set by `<MaisonIntro>` on its first mount
  //     guarantees the intro does NOT replay when navigating back to `/`
  //     from any sibling product page.
  const location = useLocation();
  const navigate = useNavigate();
  const onLogoClick = useCallback((e) => {
    setOpen(false);
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    e.preventDefault();
    navigate("/");
  }, [location.pathname, navigate]);

  return (
    <nav className="sticky top-0 z-30 backdrop-blur-xl bg-[#070709]/90 border-b border-white/[0.07]">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link
          to="/"
          onClick={onLogoClick}
          className="inline-flex items-center gap-2.5 whitespace-nowrap group"
          data-testid="nav-wordmark"
        >
          <LumetierAperture
            size={30}
            color={GOLD}
            title="Lumetier aperture mark"
          />
          <span
            className="text-[15px] sm:text-[17px] font-light tracking-[0.32em] sm:tracking-[0.46em] text-stone-50 group-hover:text-white transition-colors"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            LUMETIER
          </span>
        </Link>
        <div className="hidden md:flex items-center gap-7 text-[11px] uppercase tracking-[0.22em] whitespace-nowrap">
          <ProductsDropdown />
          {LINKS.map((l) => (
            <a key={l.href} href={l.href}
               className={l.accent ? "transition-colors" : "text-stone-400 hover:text-stone-100 transition-colors"}
               style={l.accent ? { color: GOLD } : undefined}>{l.label}</a>
          ))}
          <Link to="/admin/login" className="text-stone-400 hover:text-stone-100 transition-colors" data-testid="nav-signin">Sign in</Link>
          <a href="/marketing#apply"
             className="inline-flex items-center gap-1.5 px-4 h-9 text-xs uppercase tracking-[0.22em] text-stone-900 font-medium transition-opacity hover:opacity-85 whitespace-nowrap"
             style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` }} data-testid="nav-get-started">Get started</a>
        </div>
        <button type="button" onClick={() => setOpen((v) => !v)}
                aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
                className="md:hidden inline-flex items-center justify-center w-10 h-10 -mr-2 text-stone-200 hover:text-stone-50 transition-colors"
                data-testid="nav-menu-toggle">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/[0.07] bg-[#070709]/95 backdrop-blur-xl" data-testid="nav-mobile-panel">
          <div className="px-5 py-6 flex flex-col gap-1">
            <div className="pb-2 mb-1 border-b border-white/[0.04]">
              <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500 mb-2.5">Products</div>
              <div className="flex flex-col gap-0.5">
                {LUMETIER_PRODUCTS.map((p) => (
                  <Link key={p.id} to={p.to} onClick={() => setOpen(false)}
                        className="flex items-start gap-3 py-2 -mx-1 px-1 rounded transition-colors hover:bg-white/[0.03]"
                        data-testid={`nav-mobile-product-${p.id}`}>
                    <div className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center" style={{ background: p.iconBg }}>
                      <p.icon className="w-4 h-4" style={{ color: p.iconColor }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] text-stone-200 font-medium normal-case tracking-normal">
                        {p.title}{!p.ready && <span className="ml-2 text-[9px] uppercase tracking-[0.22em] text-stone-500">Soon</span>}
                      </div>
                      <div className="text-[11px] text-stone-500 leading-snug normal-case tracking-normal mt-0.5">{p.short}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            {LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)}
                 className={`py-3 text-[12px] uppercase tracking-[0.28em] border-b border-white/[0.04] whitespace-nowrap ${l.accent ? "" : "text-stone-300 hover:text-stone-100"}`}
                 style={l.accent ? { color: GOLD } : undefined}
                 data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</a>
            ))}
            <Link to="/admin/login" onClick={() => setOpen(false)}
                  className="py-3 text-[12px] uppercase tracking-[0.28em] text-stone-300 hover:text-stone-100 whitespace-nowrap"
                  data-testid="nav-mobile-signin">Sign in</Link>
            <a href="/marketing#apply" onClick={() => setOpen(false)}
               className="mt-4 inline-flex items-center justify-center gap-1.5 px-4 h-12 text-xs uppercase tracking-[0.28em] text-stone-900 font-medium transition-opacity hover:opacity-90 whitespace-nowrap"
               style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` }} data-testid="nav-mobile-get-started">Get started</a>
          </div>
        </div>
      )}
    </nav>
  );
}
